import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notify } from '@/lib/notify';
import { useTimeOff } from '@/stores/timeOff';
import { useStaff } from '@/stores/staff';
import { useRole, useUser } from '@/stores/identity';
import { TIME_OFF_TYPES } from '@/lib/timeOff';
import type { HalfDayPart, TimeOffType } from '@/lib/types';

/**
 * New time-off request form.
 *
 * - Staff picker is hidden when the current user is staff (auto-pinned to
 *   their own staff row).
 * - Half-day rules:
 *     same-day: Full / AM only / PM only
 *     multi-day: optionally start half (PM only) and/or end half (AM only)
 *
 * Cross-org note (audit F-04 follow-up): this form has no entity_id from
 * the URL. The staff picker is scoped to the user's home org via the
 * org-scoped staff store, and `time_off_request_create` writes the row
 * under `app_current_org()`. A platform admin can therefore only create
 * a time-off request inside their own home org — there is no cross-org
 * write path to gate, so no `<CrossOrgReadOnlyBanner />` is rendered
 * here. The banner shows up on TimeOffDetail if the admin clicks into a
 * request from another org's list.
 */
export default function TimeOffNew() {
  const navigate = useNavigate();
  const role = useRole();
  const user = useUser();

  const staffStatus = useStaff((s) => s.status);
  const staffItems = useStaff((s) => s.items);
  const loadStaff = useStaff((s) => s.load);

  const add = useTimeOff((s) => s.add);

  useEffect(() => {
    if (staffStatus === 'idle') void loadStaff();
  }, [staffStatus, loadStaff]);

  // For 'staff' role, lock to their own staff row.
  const myStaffRow = useMemo(
    () => staffItems.find((s) => s.profile_id === user?.id) ?? null,
    [staffItems, user?.id]
  );

  const [staffId, setStaffId] = useState<string>('');
  const [type, setType] = useState<TimeOffType>('vacation');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [singleDayMode, setSingleDayMode] = useState<'full' | 'am' | 'pm'>(
    'full'
  );
  const [startsHalfMulti, setStartsHalfMulti] = useState<boolean>(false);
  const [endsHalfMulti, setEndsHalfMulti] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-pin staff for staff role.
  useEffect(() => {
    if (role === 'staff' && myStaffRow && !staffId) {
      setStaffId(myStaffRow.id);
    }
  }, [role, myStaffRow, staffId]);

  const isStaff = role === 'staff';
  const sameDay = startDate && endDate && startDate === endDate;

  function buildHalves(): {
    starts_half_day: HalfDayPart | null;
    ends_half_day: HalfDayPart | null;
  } {
    if (sameDay) {
      if (singleDayMode === 'am') return { starts_half_day: 'AM', ends_half_day: null };
      if (singleDayMode === 'pm') return { starts_half_day: 'PM', ends_half_day: null };
      return { starts_half_day: null, ends_half_day: null };
    }
    return {
      starts_half_day: startsHalfMulti ? 'PM' : null,
      ends_half_day: endsHalfMulti ? 'AM' : null,
    };
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!staffId) {
      setError('Pick a staff member.');
      return;
    }
    if (!startDate || !endDate) {
      setError('Pick start and end dates.');
      return;
    }
    if (endDate < startDate) {
      setError('End date cannot be before start date.');
      return;
    }

    const { starts_half_day, ends_half_day } = buildHalves();

    setSubmitting(true);
    try {
      const created = await add({
        staff_id: staffId,
        type,
        start_date: startDate,
        end_date: endDate,
        starts_half_day,
        ends_half_day,
        reason: reason.trim() || null,
      });
      notify.success('Time-off request submitted');
      navigate(`/time-off/${created.id}`, { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Request time off
          </h1>
          <p className="text-sm text-muted-foreground">
            Submit a request for review. The owner or your location's manager
            will approve, deny, or follow up.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-5 rounded-lg border border-border bg-card p-6"
        >
          {/* Staff picker (hidden for staff role) */}
          {!isStaff && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="staffId">
                Staff member
              </label>
              <select
                id="staffId"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
              >
                <option value="">Select a staff member…</option>
                {staffItems
                  .filter((s) => !s.active_to)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.display_name}
                    </option>
                  ))}
              </select>
            </div>
          )}
          {isStaff && myStaffRow && (
            <div className="rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              Requesting for <strong>{myStaffRow.display_name}</strong>
            </div>
          )}

          {/* Type */}
          <div className="space-y-1">
            <p className="text-sm font-medium">Type</p>
            <div className="flex flex-wrap gap-2">
              {TIME_OFF_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.value}
                  onClick={() => setType(t.value)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    type === t.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border bg-background hover:bg-muted/40'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="startDate">
                Start
              </label>
              <input
                id="startDate"
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="endDate">
                End
              </label>
              <input
                id="endDate"
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Half-day controls */}
          {sameDay && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Duration</p>
              <div className="flex flex-wrap gap-2">
                {([
                  ['full', 'Full day'],
                  ['am', 'AM only'],
                  ['pm', 'PM only'],
                ] as const).map(([v, label]) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setSingleDayMode(v)}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      singleDayMode === v
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border bg-background hover:bg-muted/40'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!sameDay && startDate && endDate && (
            <div className="space-y-2 rounded-md bg-muted/40 p-3">
              <p className="block text-sm font-medium">
                Partial first / last day (optional)
              </p>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={startsHalfMulti}
                  onChange={(e) => setStartsHalfMulti(e.target.checked)}
                />
                Start in the afternoon (PM only on first day)
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={endsHalfMulti}
                  onChange={(e) => setEndsHalfMulti(e.target.checked)}
                />
                Return after the morning (AM only on last day)
              </label>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="reason">
              Reason <span className="text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="reason"
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Family wedding, doctor's appointment, etc."
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-muted/40"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
